<template>
  <mf-button :loading="loading" label="Exportar PDF" @click="print()" />
</template>

<script>
import JSPDF from 'jspdf'
import join from 'lodash/join'
import indexOf from 'lodash/indexOf'
import map from 'lodash/map'
import logo from '@/assets/logo_non_dark.png'
import { mapGetters } from 'vuex'
import { localeString } from '@/config/helpers.js'
import { DateTime } from 'luxon'

export default {
  props: {
    produtos: Array,
    primeiraData: String,
    segundaData: String,
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapGetters([
      'cuponsTotal',
      'cuponsComEncarte',
      'cuponsCliente',
      'clienteEncarte',
      'faturamentoTotal',
      'faturamentoEncarte',
      'percentualCupons',
      'ticketMedioTotal',
      'ticketMedioComEncarte',
      'percentualTicketMedio',
      'faturamentoTodosProdutos',
      'faturamentoEncarteProdutos',
      'faturamentoFora',
      'percentualFora',
      'cuponsSoEncarte',
      'ticketMedioSoEncarte',
      'cacaOfertas',
    ]),
    clientesLevados() {
      return [
        {
          text: 'Total de cupons no período',
          value: 'cuponsTotal',
          type: 'int',
        },
        {
          text: 'Cupons com pelo menos um produto do encarte',
          value: 'cuponsComEncarte',
          type: 'int',
        },
        {
          text: 'Média estimada de compras por cliente no encarte',
          value: 'cuponsCliente',
          type: 'float',
        },
        {
          text: 'Quantidade estimada de clientes trazidos pelo encarte',
          value: 'clienteEncarte',
          type: 'int',
        },
        {
          text: 'Percentual de cupons com produto(s) do encarte',
          value: 'percentualCupons',
          type: 'percent',
        },
      ]
    },
    quantoCompraram() {
      return [
        {
          text: 'Total no período',
          value: 'faturamentoTotal',
          type: 'currency',
        },
        {
          text: 'Faturamento dos cupons com produto(s) do encarte',
          value: 'faturamentoEncarte',
          type: 'currency',
        },
      ]
    },
    ticketMedio() {
      return [
        {
          text: 'Ticket médio do período',
          value: 'ticketMedioTotal',
          type: 'currency',
        },
        {
          text: 'Ticket médio dos cupons com produto(s) do encarte',
          value: 'ticketMedioComEncarte',
          type: 'currency',
        },
        {
          text: 'Aumento do ticket médio em cupons com produto(s) do encarte',
          value: 'percentualTicketMedio',
          type: 'percent',
        },
      ]
    },
    foraEncarte() {
      return [
        {
          text: 'Faturamento dos cupons com produto(s) do encarte',
          value: 'faturamentoTodosProdutos',
          type: 'currency',
        },
        {
          text: 'Faturamento dos produtos do encarte',
          value: 'faturamentoEncarteProdutos',
          type: 'currency',
        },
        {
          text: 'Faturamento dos produtos fora do encarte',
          value: 'faturamentoFora',
          type: 'currency',
        },
        {
          text: 'Percentual de produtos fora do encarte',
          value: 'percentualFora',
          type: 'percent',
        },
      ]
    },
    soEncarte() {
      return [
        {
          text: 'Cupons caça-ofertas (só com produtos do encarte)',
          value: 'cuponsSoEncarte',
          type: 'int',
        },
        {
          text: 'Ticket médio dos cupons caça-ofertas',
          value: 'ticketMedioSoEncarte',
          type: 'currency',
        },
        {
          text: 'Percentual de cupons caça-ofertas',
          value: 'cacaOfertas',
          type: 'percent',
        },
      ]
    },
    cards() {
      return [
        { name: 'Clientes levados para a loja', getter: 'clientesLevados' },
        { name: 'Faturamento', getter: 'quantoCompraram' },
        { name: 'Ticket médio de clientes do encarte', getter: 'ticketMedio' },
        { name: 'Produtos fora do encarte', getter: 'foraEncarte' },
        { name: 'Caça-ofertas', getter: 'soEncarte' },
      ]
    },
    selectedData() {
      let de = DateTime.fromISO(this.primeiraData).toFormat('dd/MM/yyyy')
      let ate = DateTime.fromISO(this.segundaData).toFormat('dd/MM/yyyy')
      return `De ${de} até ${ate}`
    },
  },
  methods: {
    getProductNames() {
      let products = []
      for (let produto of this.produtos) {
        products.push(`${produto.sku} - ${produto.name}`)
      }
      return products
    },
    formatProducts(productsArray) {
      if (productsArray.length > 2) return `Produtos: ${productsArray[0]} e mais ${productsArray.length - 2} outros produtos selecionados`
      if (productsArray.length === 2) return 'Produtos: ' + join(productsArray, ', ')
      return 'Produto: ' + productsArray
    },
    formatString(getter) {
      if (getter.type === 'float') return localeString(this[getter.value], 1)
      if (getter.type === 'currency') return `R$ ${localeString(this[getter.value], 2)}`
      if (getter.type === 'percent') return `${localeString(this[getter.value], 1)}%`
      if (getter.type === 'int') return localeString(this[getter.value], 0)
    },
    print() {
      this.loading = true
      try {
        let doc = new JSPDF()
        doc.addImage(logo, 'PNG', 160, 11, 32, 10)
        doc.text('Resultados de Encarte', 10, 16)
        doc.setFontSize(8)
        doc.text(this.selectedData, 10, 25)
        doc.text(this.formatProducts(this.getProductNames()), 10, 30)
        for (let card of this.cards) {
          let yIndex = 42 + 48 * indexOf(this.cards, card)
          doc.setFontSize(12)
          doc.text(card.name, 10, yIndex)
          doc.setFontSize(8)
          doc.text(map(this[card.getter], 'text'), 10, yIndex + 15)
          let cardArray = []
          for (let getterName of this[card.getter]) {
            cardArray.push(String(this.formatString(getterName)))
          }
          doc.text(cardArray, 120, yIndex + 15, 'right')
        }
        let graphsArray = document.getElementsByClassName('chartjs-render-monitor')
        for (let graph of graphsArray) {
          let lineYIndex = 34 + 48 * indexOf(graphsArray, graph)
          doc.addImage(graph, 'PNG', 135, lineYIndex + 2, 65, 45)
          doc.line(10, lineYIndex, 200, lineYIndex)
        }
        let now = DateTime.now().toFormat('dd-MM-yy')
        doc.save('resultados-de-encarte-' + now + '.pdf')
      } catch (error) {
        this.$snackbar({ message: 'Erro ao exportar PDF', snackbarColor: 'danger' })
      } finally {
        this.loading = false
      }

      this.loading = false
    },
  },
}
</script>

<style lang="scss"></style>
